import * as React from 'react'
import { components } from 'react-select'
import { SelectComponents } from 'react-select/src/components'
import styled, { css } from 'styled-components'
import ExpenseCategoryHelper from '../../helpers/ExpenseCategoryHelper'
import { ExpenseCategory } from '../../types'
import Icon from '../Icons/Icon'
import GroupHeading from './GroupHeading'
import ResourceSelectComponents from './ResourceSelectComponents'
import MultiValueContainer from './Parts/MultiValueContainer'
import ValueContainer from './Parts/ValueContainer'
import ValueActions from './Parts/ValueActions'
import ValueAction from './Parts/ValueAction'

const SelectableGroupHeading = styled(GroupHeading) <{ selectable: boolean, selected: boolean }>`
  ${props => props.selectable && css`
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  `}

  ${props => props.selected && css`
    font-weight: 600;
  `}
`

const GroupHeadingActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`

const EditCategoryAction = styled.div`
  cursor: pointer;

  svg, i {
    color: rgb(204, 204, 204);
    fill: rgb(204, 204, 204);
  }
`

export const ExpenseCategoryTreeIcon = () => (
  <svg width="8px" height="12px" viewBox="0 0 8 12" version="1.1" >
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g id="Group" stroke="#D8D8D8">
        <rect id="Rectangle" fill="#D8D8D8" x="0.5" y="0.5" width="1" height="3"></rect>
        <rect id="Rectangle" fill="#D8D8D8" x="0.5" y="7.5" width="1" height="4"></rect>
        <rect id="Rectangle" fill="#D8D8D8" x="4.5" y="10.5" width="3" height="1"></rect>
      </g>
    </g>
  </svg>
)

const CategoryIcon = ({ category }: { category?: ExpenseCategory }) => {
  if (!category) return null

  return ExpenseCategoryHelper.getIcon(category, category.color)
}

const ExpenseCategorySelectComponents: Partial<SelectComponents<any, any>> = {
  ...ResourceSelectComponents,
  GroupHeading: (props) => {
    const { data, selectProps: { upsertModeEnabled, onEditClick, onGroupHeadingClick, isGroupHeadingSelectable, isGroupSelected } } = props
    const category: ExpenseCategory | undefined = data.category
    const selected = isGroupHeadingSelectable && isGroupSelected(data.value)

    return (
      <SelectableGroupHeading
        selectable={isGroupHeadingSelectable}
        selected={selected}
        onMouseDown={isGroupHeadingSelectable ? (e) => {
          // Keep the menu open and prevent the input from losing focus
          e.preventDefault()
          e.stopPropagation()
          onGroupHeadingClick(data.value)
        } : undefined}
      >
        <div style={{ marginBottom: 4 }}>
          <CategoryIcon category={category} />
        </div>
        <components.GroupHeading {...props} />
        <GroupHeadingActions>
          {upsertModeEnabled && category?.editable && <EditCategoryAction
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onEditClick(e, data)
            }}
          >
            <Icon icon='pencil' />
          </EditCategoryAction>}
          {selected && <Icon icon='check' />}
        </GroupHeadingActions>
      </SelectableGroupHeading>
    )
  },
  Option: (props) => {
    const { data, selectProps: { upsertModeEnabled, onEditClick } } = props
    const { label, __isNew__, placeholder, category } = data

    if (placeholder) return null

    if (__isNew__) {
      return (
        <components.Option {...props}>
          {label}
        </components.Option>
      )
    }

    return (
      <components.Option {...props}>
        <div className='powerselect-expense-category-option-container'>
          <ExpenseCategoryTreeIcon />
          {label}

          {upsertModeEnabled && category?.editable && <EditCategoryAction style={{ marginLeft: 8 }} onClick={(e) => onEditClick(e, data)}>
            <Icon icon='pencil' />
          </EditCategoryAction>}
        </div>
      </components.Option>
    )
  },
  SingleValue: (props) => {
    const { data: { label, category } } = props

    return (
      <components.SingleValue {...props}>
        <div className='powerselect-expense-category-selected-value-container'>
          <CategoryIcon category={category} />
          {label}
        </div>
      </components.SingleValue>
    )
  },
  MultiValue: (props) => {
    const { data, selectProps: { upsertModeEnabled, onEditClick }, isDisabled } = props
    const { label, category } = data

    return (
      <components.MultiValue {...props}>
        <MultiValueContainer>
          <ValueContainer isMulti>
            <CategoryIcon category={category} />
            <span>{label}</span>
          </ValueContainer>
          {!isDisabled && upsertModeEnabled && category?.editable && <ValueActions>
            <ValueAction onClick={(e) => onEditClick(e, data)} onTouchStart={(e) => onEditClick(e, data)}>
              <Icon icon='pen' />
            </ValueAction>
          </ValueActions>}
        </MultiValueContainer>
      </components.MultiValue>
    )
  },
  IndicatorsContainer: (props) => {
    const { selectProps, isMulti, hasValue, getValue } = props
    const selectedOption: any = hasValue ? getValue()[0] : null

    // Default categories are not editable, so hide the edit action for them
    const upsertModeEnabled = selectProps.upsertModeEnabled && (isMulti || !hasValue || Boolean(selectedOption?.category?.editable))
    const BaseIndicatorsContainer = ResourceSelectComponents.IndicatorsContainer

    return <BaseIndicatorsContainer {...props} selectProps={{ ...selectProps, upsertModeEnabled }} />
  },
}

export default ExpenseCategorySelectComponents
